import * as React from "react";
import { useState, useEffect } from "react";
import type { FC } from "react";
import { Hint, Spinner } from "react-ui";
import { SingleToast } from "@skbkontur/react-ui";

import { AttachFormat, AttachInfo } from "../../../../../../Common/CandyApiClient/Types/Attachments/Attacments";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import { useBeforeunload } from "../../../Commons/DOM/UseWindowEventListener";
import { reject } from "../../../../../../Common/TypingUtils";
import { useDetectTextOverflowed } from "../../../FormParts/HiddenPanel/ReactEffects";
import { ofReactType } from "../../../TypingUtils";

import { FileName, FileNameAndErrorsContainer, MessageContainer, Interactive, ActionsBlock } from "./ViewFile.styles";
import { ViewFileError } from "./ViewFileError/ViewFileError";
import { ViewFileWarning } from "./ViewFileWarning/ViewFileWarning";
import { FileError } from "./FileError";
import { FnsRequirementsModal } from "./FnsRequirementsModal/FnsRequirementsModal";
import { getProgressValue, UploadFileProgress } from "./UploadFileProgress/UploadFileProgress";
import { RemoveAttachmentButton } from "./RemoveAttachmentButton/RemoveAttachmentButton";
import { AttachmentIcon } from "./AttachmentIcon/AttachmentIcon";
import { DownloadButton } from "./Actions";
import { RemoveButton } from "./RemoveButton";
import { ConvertationStatus, ConvertationType } from "./Convertation/Types";
import { ViewFileConversion } from "./ViewFileConversion/ViewFileConversion";

export interface ViewFileProps {
    bindingPath: AbsoluteModelPath;
    parentPath: AbsoluteModelPath;
    width?: number;
    attachment: undefined | AttachInfo;
    fileName: string;
    error?: FileError;
    warning?: FileError;
    downloadable: boolean;
    hideActions: boolean;
    withGosEdoConversion: boolean;
    onRemoveAttachment: () => void;
    onFileDownload: (uri: string, fileName: string) => void;
    onAttachmentLoaded: (attachment: Required<AttachInfo>, convertationType?: ConvertationType) => void;
    convertationStatus?: ConvertationStatus;
    setConvertationStatus?: (status: ConvertationStatus) => void;
    children?: React.ReactNode;
    extentions?: readonly string[];
    convertationType?: ConvertationType;
    isReadOnly?: boolean;
}

export const ViewFile: FC<ViewFileProps> = props => {
    const { bindingPath, error, warning, attachment, extentions, fileName, convertationType, convertationStatus } =
        props;

    const progressValue = getProgressValue(attachment);

    const isValid = !error && !warning;

    const convertationFormat = attachment?.meta?.format;

    const fileConverted =
        convertationType === ConvertationType.PdfA1_1_4
            ? convertationFormat === AttachFormat.PdfA1_1_4
            : convertationType === ConvertationType.Pdf
              ? convertationFormat === AttachFormat.Pdf
              : true;

    const getTargetFormat = (type?: ConvertationType): AttachFormat | undefined => {
        if (type === ConvertationType.Pdf) {
            return AttachFormat.Pdf;
        }
        if (type === ConvertationType.PdfA1_1_4) {
            return AttachFormat.PdfA1_1_4;
        }
        return undefined;
    };

    const uploadingInProgress = progressValue != undefined && isValid;
    const uploadingDone = !uploadingInProgress && isValid;
    const fileReady = uploadingDone && fileConverted;

    const hasConversionError =
        convertationStatus === ConvertationStatus.Failed || convertationStatus === ConvertationStatus.ServerError;

    const canDownload = fileReady && props.downloadable;
    const showSpinner = !fileReady && !props.hideActions && isValid && !hasConversionError;

    useBeforeunload(!fileReady && isValid, "Часть документов не догрузились. Остаться на странице?");

    useEffect(() => {
        if (uploadingDone) {
            const needsConversion =
                props.convertationType !== undefined &&
                attachment?.meta?.format !== getTargetFormat(props.convertationType);
            if (fileConverted && needsConversion) {
                props.setConvertationStatus?.(ConvertationStatus.Success);
            }
        }
    }, []);

    useEffect(() => {
        if (uploadingDone && attachment?.contentUri) {
            props.onAttachmentLoaded(
                // @ts-expect-error проверка выше гарантирует наличие uri, в данном сценарии этого достаточно
                attachment,
                convertationType
            );
        }
    }, [uploadingDone, attachment?.contentUri, convertationType]);

    // NOTE: мемоизировать при рефакторинге компонентов FileLoader и ViewFile
    const customRemoveButton = React.Children.toArray(props.children).find(ofReactType(RemoveButton));

    return (
        <Interactive hideActions={props.hideActions}>
            <AttachmentIcon error={error != undefined} warning={warning != undefined} />
            <FileNameAndErrorsContainer>
                <FileNameWithHint text={props.fileName} />
                {uploadingInProgress && <UploadFileProgress progress={progressValue ?? reject()} />}
                {convertationType === ConvertationType.Pdf && (
                    <>
                        <SingleToast />
                        <ViewFileConversion convertationStatus={convertationStatus} attachment={attachment} />
                    </>
                )}
                {!isValid && (
                    <Message
                        bindingPath={bindingPath}
                        error={error}
                        warning={warning}
                        extentions={extentions}
                        fileName={fileName}
                    />
                )}
            </FileNameAndErrorsContainer>
            <ActionsBlock>
                {showSpinner ? (
                    <Spinner type="mini" color="black" width={1} caption="" />
                ) : (
                    <>
                        {canDownload && (
                            <DownloadButton
                                onClick={() => {
                                    if (attachment?.contentUri) {
                                        props.onFileDownload(attachment.contentUri, props.fileName);
                                    }
                                }}
                            />
                        )}
                        {!props.isReadOnly && (
                            <RemoveAttachmentButton
                                onRemove={props.onRemoveAttachment}
                                metricsContext={{ path: bindingPath.toLegacyPath(), fileName: attachment?.file?.name }}
                                customElement={customRemoveButton}
                            />
                        )}
                    </>
                )}
            </ActionsBlock>
        </Interactive>
    );
};

const FileNameWithHint: FC<{ text: string }> = ({ text }) => {
    const [fileNameRef, fileNameHasOverflow] = useDetectTextOverflowed({ liveMeasure: false });

    return (
        <Hint useWrapper={false} pos={"top"} text={text} manual={!fileNameHasOverflow} maxWidth={400}>
            <FileName ref={fileNameRef} data-tid="FileName">
                {text}
            </FileName>
        </Hint>
    );
};

const Message: FC<{
    bindingPath: AbsoluteModelPath;
    error: FileError | undefined;
    warning: FileError | undefined;
    extentions?: readonly string[];
    fileName?: string;
}> = ({ bindingPath, error, warning, extentions, fileName }) => {
    const [showFnsRequirements, setShowFnsRequirements] = useState(false);

    return (
        <>
            {showFnsRequirements && <FnsRequirementsModal onClose={() => setShowFnsRequirements(false)} />}
            <MessageContainer>
                {error && (
                    <ViewFileError
                        error={error}
                        path={bindingPath}
                        onClickRequirements={() => setShowFnsRequirements(true)}
                        extentions={extentions}
                        fileName={fileName}
                    />
                )}
                {warning && !error && (
                    <ViewFileWarning
                        path={bindingPath}
                        warning={warning}
                        onClickRequirements={() => setShowFnsRequirements(true)}
                    />
                )}
            </MessageContainer>
        </>
    );
};
